<script setup lang="ts">
import { ref, onMounted, reactive } from 'vue'
import { Button } from '@/components/ui/button'
import { useRouter, useRoute } from 'vue-router'
import { useAuth } from '@/composables/useAuth'
import MainLayout from '@/components/layout/MainLayout.vue'
import { Input } from '@/components/ui/input'
import { SignaturePad } from '@/components/ui/signature-pad'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Label } from '@/components/ui/label'
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert'
import { api } from '@/lib/api'
import { useFormValidation } from '@/composables/useFormValidation'
import { validateName, validatePPSN } from '@/lib/validation'

const router = useRouter()
const route = useRoute()
const { user, refreshProfile } = useAuth()

const error = ref('')
const submitting = ref(false)
const isOptedOut = ref(false)

// Form data
const formData = reactive({
  firstName: user.value?.first_name ?? '',
  lastName: user.value?.surname ?? '',
  ppsn: user.value?.ppsn || '',
  dateOfBirth: user.value?.date_of_birth || '',
  emailAddress: user.value?.email || '',
  taxAgencyName: 'Maximo Financial Planning Ltd',
  tain: '123456', // This should be your actual TAIN
  agentAddress: 'Ballyfoyle, Co.Kilkenny\nX42 620',
  documentHolder: 'self',
  clientSignature: '',
  clientSignatureDate: new Date().toISOString().split('T')[0],
  agentSignature: 'Richie Walsh',
  agentSignatureDate: new Date().toISOString().split('T')[0],
})

// Validation setup
const validationSchema = {
  firstName: validateName,
  lastName: validateName,
  ppsn: validatePPSN,
}

const {
  errors,
  isValid,
  handleFieldTouch,
  markAllFieldsTouched,
  getInputClass,
} = useFormValidation(validationSchema, formData)

onMounted(() => {
  // Check if user has opted out of PAYE authorization
  if (user.value?.opt_out_paye_auth) {
    isOptedOut.value = true
  }
})

const handleAuthorize = async () => {
  markAllFieldsTouched()
  if (!isValid.value) return

  if (!user.value) return

  submitting.value = true
  error.value = ''

  try {
    await api.post('/paye-authorization', {
      authorized: true,
      authorized_at: new Date().toISOString(),
      form_data: {
        firstName: formData.firstName,
        lastName: formData.lastName,
        ppsn: formData.ppsn,
        dateOfBirth: formData.dateOfBirth,
        emailAddress: formData.emailAddress,
        taxAgencyName: formData.taxAgencyName,
        tain: formData.tain,
        agentAddress: formData.agentAddress,
        documentHolder: formData.documentHolder,
        clientSignature: formData.clientSignature,
        clientSignatureDate: new Date().toISOString(),
        agentSignature: formData.agentSignature,
        agentSignatureDate: formData.agentSignatureDate,
      },
    })

    // Update user profile to ensure opt_out_paye_auth is set to false
    await api.post('/edit-profile', {
      first_name: user.value.first_name ?? formData.firstName,
      surname: user.value.surname ?? formData.lastName,
      ppsn: user.value.ppsn ?? formData.ppsn,
      opt_out_paye_auth: false,
    })

    await refreshProfile()

    const returnTo = route.query.returnTo as string
    if (returnTo) {
      router.push(returnTo)
    } else {
      router.push('/tax-refund-questions')
    }
  } catch (e) {
    error.value = 'Something went wrong. Please try again.'
    console.error(e)
  } finally {
    submitting.value = false
  }
}

const handleDecline = () => {
  router.push('/tax-refund-options')
}

const handleUpdatePreference = async () => {
  submitting.value = true
  error.value = ''

  try {
    // Update user profile with preference
    await api.post('/edit-profile', {
      opt_out_paye_auth: false,
    })

    await refreshProfile()
    isOptedOut.value = false
  } catch (e) {
    error.value = 'Failed to update preference. Please try again.'
    console.error(e)
  } finally {
    submitting.value = false
  }
}
</script>

<template>
  <MainLayout>
    <div class="max-w-4xl mx-auto p-6 space-y-8">
      <!-- Show a message if the user has opted out -->
      <Alert v-if="isOptedOut" class="bg-amber-50 border-amber-200">
        <AlertTitle class="text-amber-800"
          >PAYE Authorization Opt Out</AlertTitle
        >
        <AlertDescription class="text-amber-700">
          You have chosen to opt out of PAYE authorization. This means we cannot
          process tax refunds on your behalf. If you would like to proceed with
          tax refund processing, you can update your preference below.
        </AlertDescription>
        <div class="mt-4">
          <Button @click="handleUpdatePreference" :disabled="submitting">
            Enable PAYE Authorization
          </Button>
        </div>
      </Alert>

      <div
        v-if="!isOptedOut"
        class="bg-white rounded-lg shadow-md p-8 space-y-6"
      >
        <div class="flex justify-between items-center">
          <h1 class="text-2xl font-bold">
            Declaration to Revenue - Authorization Form PAYE A1
          </h1>
          <!-- <img src="/images/revenue-logo.png" alt="Revenue" class="h-16 object-contain" /> -->
        </div>

        <p class="text-sm text-gray-600">
          For information on how an agent can use Revenue systems on your
          behalf, please see 'Using a tax agent or tax service' on
          www.revenue.ie.
        </p>

        <div class="space-y-6">
          <section class="space-y-4">
            <h2 class="text-xl font-semibold">
              1. Authorization to Act as Agent
            </h2>

            <div class="grid grid-cols-1 gap-4">
              <div class="space-y-2">
                <Label>I,</Label>
                <Input
                  v-model="formData.firstName"
                  placeholder="First name"
                  :class="getInputClass('firstName')"
                  @blur="handleFieldTouch('firstName')"
                />
                <p v-if="errors.firstName" class="text-sm text-red-500">
                  {{ errors.firstName }}
                </p>
                <Input
                  v-model="formData.lastName"
                  placeholder="Surname"
                  :class="getInputClass('lastName')"
                  @blur="handleFieldTouch('lastName')"
                />
                <p v-if="errors.lastName" class="text-sm text-red-500">
                  {{ errors.lastName }}
                </p>
              </div>

              <div class="space-y-2">
                <Label>PPSN</Label>
                <Input
                  v-model="formData.ppsn"
                  placeholder="PPSN"
                  :class="getInputClass('ppsn')"
                  @blur="handleFieldTouch('ppsn')"
                />
                <p v-if="errors.ppsn" class="text-sm text-red-500">
                  {{ errors.ppsn }}
                </p>
              </div>

              <div class="space-y-2">
                <Label>Date of Birth</Label>
                <Input type="date" v-model="formData.dateOfBirth" />
              </div>

              <div class="space-y-2">
                <Label>E-mail address (of taxpayer) (mandatory)</Label>
                <Input type="email" v-model="formData.emailAddress" />
              </div>
            </div>

            <p class="text-gray-700">authorize</p>
            <div class="bg-gray-50 p-4 rounded-lg space-y-2">
              <p>{{ formData.taxAgencyName }}</p>
              <p>TAIN: {{ formData.tain }}</p>
              <p class="whitespace-pre-line">
                {{ formData.agentAddress }}
              </p>
            </div>

            <p class="text-gray-700">
              to act as my agent in dealing with all aspects of the filing of my
              Irish income tax return, including the submission of refund or
              credit claims, allowances or reliefs.
            </p>

            <div class="space-y-4">
              <p class="text-gray-700">
                I confirm that all documentary evidence of entitlement to
                credits / reliefs claimed and taxable income sources, will be
                held for a period of 6 years beginning at the end of the year of
                assessment to which the Return of Income and / or claim relates
                by:
              </p>

              <RadioGroup v-model="formData.documentHolder" class="space-y-2">
                <div class="flex items-center space-x-2">
                  <RadioGroupItem value="self" id="self" />
                  <Label for="self">Myself</Label>
                </div>
              </RadioGroup>
              (mandatory)
            </div>

            <p class="text-gray-700">
              I confirm that this authorisation will remain in force until
              Revenue is formally notified of its cessation by either myself or
              {{ formData.taxAgencyName }}, or for a maximum period of 4 years.
              [The submission of an updated Authorization Form after 4 years
              will renew the authorization.]
            </p>
          </section>

          <section class="space-y-4">
            <h2 class="text-xl font-semibold">
              2. Terms and Conditions of Authorization
            </h2>

            <p class="text-gray-700">
              I understand that Tax law provides for both civil penalties and
              criminal sanctions for the failure to make a return, the making of
              a false return, facilitating the making of a false return, or
              claiming tax credits, allowances or reliefs which are not due.
            </p>

            <p class="text-gray-700">
              I confirm that I will provide the necessary documentation to
              {{ formData.taxAgencyName }} to support any refund, credit claims
              or claims for allowances and reliefs made to Revenue on my behalf
              by {{ formData.taxAgencyName }}.
            </p>

            <p class="text-gray-700">
              I confirm that I will provide details of all my sources of income
              to {{ formData.taxAgencyName }}.
            </p>

            <p class="text-gray-700">
              I understand that the person selected in Section 1 above is
              required to retain all documentation relating to any refund or
              credit or allowance or relief claimed by the agent on my behalf
              for a period of 6 years beginning at the end of the year of
              assessment to which the Return of Income and / or claim relates
              and that {{ formData.taxAgencyName }} will be required to produce
              same to Revenue upon request.
            </p>
          </section>

          <div class="grid grid-cols-1 gap-4">
            <div class="space-y-2">
              <Label>Signed (Client)</Label>
              <SignaturePad v-model="formData.clientSignature" />
            </div>

            <div class="space-y-2 w-1/3 text-gray-700">
              <p>
                {{
                  Intl.DateTimeFormat('en-IE', { dateStyle: 'full' }).format(
                    new Date(formData.clientSignatureDate),
                  )
                }}
              </p>
            </div>
          </div>
        </div>

        <div class="space-y-4">
          <p v-if="error" class="text-sm text-red-500">
            {{ error }}
          </p>

          <div class="flex gap-4">
            <Button
              variant="outline"
              size="lg"
              class="flex-1"
              @click="handleDecline"
              :disabled="submitting"
            >
              Decline
            </Button>

            <Button
              size="lg"
              class="flex-1"
              @click="handleAuthorize"
              :disabled="
                submitting ||
                !formData.firstName ||
                !formData.lastName ||
                !formData.clientSignature
              "
            >
              Authorize
            </Button>
          </div>
        </div>
      </div>
    </div>
  </MainLayout>
</template>
